import { ButtonTertiary, ButtonPrimary, IconButton } from "@nova/component-btn"
import { ExpandableContent } from "@nova/component-expandable-content"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { ChecklistSubmission } from "@nova/schemas"
import classNames from "classnames"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useRouter } from "pages"
import { useMemo, useState } from "react"
import { useParams } from "react-router"
import { DateFilter } from "../filters/DateFilter"
import { SubmitterFilter } from "../filters/SubmitterFilter"
import {
    selectChecklistDateFilter,
    selectChecklistPilotFilter,
} from "./checklist-details/selectors"
import {
    useChecklistSubmissions,
    useChecklistSubmitters,
    useDownloadChecklistSubmission,
} from "./ChecklistSubmissionQueries"
import { useMission } from "../flights-page/PilotTrackingMissionQueries"
import { formatTime, TimeFormat } from "@nova/format"
import * as Icons from "@nova/component-icons"
import { setDateFilter, setPilotFilter } from "./checklist-details/slice"
import { pluralize } from "@nova/util-strings"

type UserGroup = { userId: string; submissions: ChecklistSubmission[] }
type DateGroup = { date: string; userGroups: UserGroup[] }

function groupSubmissions(submissions: ChecklistSubmission[]): DateGroup[] {
    const dateOrder: string[] = []
    const byDate = new Map<string, Map<string, ChecklistSubmission[]>>()

    for (const s of submissions) {
        const date = new Date(s.filledAt).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        })
        if (!byDate.has(date)) {
            dateOrder.push(date)
            byDate.set(date, new Map())
        }
        const byUser = byDate.get(date)!
        if (!s.userId) continue
        if (!byUser.has(s.userId)) byUser.set(s.userId, [])
        byUser.get(s.userId)!.push(s)
    }

    return dateOrder.map((date) => {
        const userMap = byDate.get(date)!
        return {
            date,
            userGroups: Array.from(userMap.entries()).map(([userId, subs]) => ({
                userId,
                submissions: subs,
            })),
        }
    })
}

export function ChecklistResponsesSection() {
    const dispatch = useAppDispatch()
    const { checklistId = "" } = useParams()
    const members = useAppSelector(selectWorkspaceMembers)
    const dateFilter = useAppSelector(selectChecklistDateFilter)
    const pilotFilter = useAppSelector(selectChecklistPilotFilter)
    const { userIds, isLoading: isLoadingSubmitters } = useChecklistSubmitters({ checklistId })
    const { submissions, hasMore, isLoading, loadMore } = useChecklistSubmissions({
        checklistId,
        dateFrom: dateFilter?.from,
        dateTo: dateFilter?.to,
        userId: pilotFilter?.id,
    })
    const dateGroups = useMemo(() => groupSubmissions(submissions), [submissions])

    const submitters = useMemo(
        () =>
            userIds.flatMap((id) => {
                const member = members?.find((m) => m.userId === id)
                if (!member) return []
                return [{ id, name: member.fullName ?? id }]
            }),
        [userIds, members]
    )

    if (isLoading || isLoadingSubmitters) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    if (!isLoadingSubmitters && userIds.length === 0) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <h4>No Responses</h4>
            </div>
        )
    }

    return (
        <div className="flex-1 flex gap-8">
            <div className="shrink-0 w-[240px] py-8 flex flex-col gap-2">
                <small className="xs text-gray-300">Filters</small>
                <DateFilter
                    value={dateFilter ?? undefined}
                    onChange={(v) => dispatch(setDateFilter(v ?? null))}
                />
                <SubmitterFilter
                    users={submitters}
                    value={pilotFilter ?? undefined}
                    onChange={(v) => dispatch(setPilotFilter(v ?? null))}
                />
            </div>
            {!!dateGroups.length ? (
                <ScrollableArea className="py-8 flex-1">
                    <div className="flex flex-col gap-8">
                        {dateGroups.map((group) => (
                            <div key={group.date} className="flex flex-col gap-4">
                                <div className="text-white text-[16px]">{group.date}</div>
                                <div className="flex flex-col gap-2">
                                    {group.userGroups.flatMap((userGroup) =>
                                        userGroup.submissions.map((submission) => (
                                            <ChecklistSubmissionRow
                                                key={submission.id}
                                                submission={submission}
                                                userId={userGroup.userId}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="text-gray-400 text-sm text-center py-4">Loading...</div>
                        )}
                        {hasMore && !isLoading && (
                            <ButtonTertiary
                                title="Load More"
                                RightIcon={Icons.ArrowRight}
                                rightIconClassName="rotate-90"
                                onClick={loadMore}
                            />
                        )}
                    </div>
                </ScrollableArea>
            ) : (
                <div className="flex items-center justify-center flex-1">
                    <h4>No Responses</h4>
                </div>
            )}
        </div>
    )
}

function ChecklistSubmissionRow({
    submission,
    userId,
}: {
    submission: ChecklistSubmission
    userId: string
}) {
    const [isExpanded, setIsExpanded] = useState(false)
    const members = useAppSelector(selectWorkspaceMembers)
    const member = members?.find((m) => m.userId === userId)
    const fullName = member?.fullName ?? ""
    const flightCount = submission.missionIds.length
    const { downloadChecklistSubmission, isDownloading } = useDownloadChecklistSubmission()

    const handleDownload = (e: { stopPropagation(): void }) => {
        e.stopPropagation()
        void downloadChecklistSubmission({
            checklistId: submission.checklistId,
            submissionId: submission.id,
        })
    }

    return (
        <div
            className={classNames(
                "rounded-lg border border-white-100 overflow-hidden transition-colors",
                {
                    "bg-white-100": isExpanded,
                }
            )}
        >
            <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white-100 transition-colors group"
                onClick={() => setIsExpanded((v) => !v)}
            >
                <Icons.Icon
                    Icon={Icons.ArrowDropdownRight}
                    className={classNames("transition-[transform,color] shrink-0", {
                        "text-white-500": !isExpanded,
                        "rotate-90 text-white": isExpanded,
                    })}
                />
                <div className="w-8 h-8 rounded-md overflow-hidden shrink-0">
                    <ProfilePhoto userId={userId} fullName={fullName} />
                </div>
                <div className="flex-1 text-gray-50">{fullName || "Unknown User"}</div>
                <div
                    className={classNames("text-gray-300 font-mono", {
                        "group-hover:hidden": !isExpanded,
                    })}
                >
                    {pluralize({ count: flightCount, str: "flight" })}
                </div>
                <ButtonPrimary
                    title="Download"
                    LeftIcon={Icons.Download}
                    disabled={isDownloading}
                    className={classNames("hidden", {
                        "group-hover:flex": !isExpanded,
                    })}
                    onClick={handleDownload}
                />
            </div>
            <ExpandableContent isOpen={isExpanded}>
                <div className="flex flex-col pl-[34px]">
                    {submission.missionIds.map((missionId) => (
                        <FlightRow key={missionId} missionId={missionId} />
                    ))}
                    {!submission.missionIds.length && (
                        <h4 className="w-full py-5 text-gray-400 text-center">
                            No flights assigned yet
                        </h4>
                    )}
                </div>
                <div className="w-full h-px bg-white-100" />
                <div className="p-4 flex justify-end">
                    <ButtonPrimary
                        title="Download"
                        LeftIcon={Icons.Download}
                        disabled={isDownloading}
                        onClick={handleDownload}
                    />
                </div>
            </ExpandableContent>
        </div>
    )
}

function FlightRow({ missionId }: { missionId: string }) {
    const router = useRouter("/workspace/:workspaceId")
    const { mission, isLoading } = useMission({ missionId })
    if (isLoading) return <div className="h-10 p-2">Loading...</div>
    if (!mission) return null
    return (
        <div className="p-2 text-sm text-gray-100 hover:text-white hover:bg-white-100 rounded-l-md flex gap-4 items-center group/flight-row">
            <div>
                @ {formatTime({ value: mission.started_at, format: TimeFormat.TwentyFourHour })}
            </div>
            <div>{formatDuration(mission.flightTime)}</div>
            <div>{mission.aircraft?.name ?? "Unknown Aircraft"}</div>
            <div className="grow" />
            <router.Link to={{ path: "/fleet/flights/:missionId", params: { missionId } }}>
                <IconButton className="hidden group-hover/flight-row:block">
                    <div className="flex items-center gap-1 text-gray-100 hover:text-white">
                        <div>View Flight</div>
                        <Icons.SmallIcon Icon={Icons.ArrowRight} />
                    </div>
                </IconButton>
            </router.Link>
        </div>
    )
}

function formatDuration(seconds: number | null | undefined) {
    if (seconds == null) return "—"
    const mins = Math.round(seconds / 60)
    if (mins < 60) return `${mins} min`
    const hours = Math.floor(mins / 60)
    const rem = mins % 60
    return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`
}
